'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { TableCell, TableRow } from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { isColorLight } from '@/helpers/color-utils'
import { getUserProfilePictureUrl } from '@/helpers/user'
import { getDriverDisplayIdentifierDisplay } from '@/lib/driver-display-identifiers'
import { cn } from '@/lib/utils'
import { Fragment } from 'react'
import StandingsDriverPointsCell from '../cells/standings-driver-points-cell'
import StandingsDriverPosCell from '../cells/standings-driver-pos-cell'

export default function StandingsTeamTableRow({
  rowIndex,
  result,
  showSplits,
  showCarClasses,
  handleUserClicked,
  driverDisplayIdentifier,
}) {
  return (
    <>
      <DesktopRow
        rowIndex={rowIndex}
        result={result}
        showSplits={showSplits}
        showCarClasses={showCarClasses}
        handleUserClicked={handleUserClicked}
        driverDisplayIdentifier={driverDisplayIdentifier}
      />

      <MobileRow
        rowIndex={rowIndex}
        result={result}
        showSplits={showSplits}
        showCarClasses={showCarClasses}
        driverDisplayIdentifier={driverDisplayIdentifier}
      />
    </>
  )
}

function DesktopRow({
  rowIndex,
  result,
  showSplits,
  showCarClasses,
  handleUserClicked,
  driverDisplayIdentifier,
}) {
  return (
    <TableRow className="hidden h-full grow md:table-row">
      <TableCell>
        <StandingsDriverPosCell position={rowIndex + 1} />
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-3 font-semibold">
          <div className="relative size-10 min-w-10 overflow-hidden rounded-full">
            <ImageWithFallback src={result.team.logoImgUrl} fill />
          </div>
          {result.team.name}
        </div>
      </TableCell>

      <TableCell>
        <TooltipProvider>
          <div className="flex flex-wrap items-center gap-1">
            {result.members.map((user) => (
              <Tooltip key={user.id} delayDuration={0}>
                <TooltipTrigger asChild>
                  <div
                    className="relative size-10 min-w-10 cursor-pointer overflow-hidden rounded-full hover:z-10"
                    onClick={() => handleUserClicked(user)}
                  >
                    <ImageWithFallback
                      src={getUserProfilePictureUrl(user, {
                        profile: 'driver',
                      })}
                      fill
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {getDriverDisplayIdentifierDisplay(
                    user,
                    driverDisplayIdentifier,
                  )}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>
      </TableCell>

      {showSplits && (
        <TableCell>
          <div className="flex items-start">
            {result.splits.map((split) => (
              <Fragment key={split.id}>
                {split ? (
                  <div
                    className="rounded-full px-4 py-2 text-p-1 font-semibold"
                    style={{
                      backgroundColor: split.color ?? '#ffffff',
                      color: isColorLight(split.color ?? '#ffffff')
                        ? '#000000'
                        : '#ffffff',
                    }}
                  >
                    {split.name}
                  </div>
                ) : (
                  '-'
                )}
              </Fragment>
            ))}
          </div>
        </TableCell>
      )}

      {showCarClasses && (
        <TableCell>
          <div className="flex items-start">
            {result.carClasses.length > 0
              ? result.carClasses.map((cc) => cc.name).join(', ')
              : '-'}
          </div>
        </TableCell>
      )}

      <TableCell className="text-end">
        <StandingsDriverPointsCell
          position={rowIndex + 1}
          points={result.total}
        />
      </TableCell>
    </TableRow>
  )
}

function MobileRow({
  rowIndex,
  result,
  showSplits,
  showCarClasses,
  driverDisplayIdentifier,
}) {
  return (
    <TableRow className="flex h-full grow justify-between md:hidden">
      <TableCell>
        <StandingsDriverPosCell position={rowIndex + 1} />
      </TableCell>

      <TableCell
        className="grow"
        colSpan={2 + Number(showSplits) + Number(showCarClasses)}
      >
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value={result.team.id} className="m-0 border-none">
            <AccordionTrigger>
              <div className="flex items-center gap-3 font-semibold">
                <div className="relative size-10 min-w-10 overflow-hidden rounded-full">
                  <ImageWithFallback src={result.team.logoImgUrl} fill />
                </div>
                {result.team.name}
              </div>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4">
              <TooltipProvider>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Drivers</span>

                  <div className="flex items-center">
                    {result.members.map((user, index) => (
                      <Tooltip key={user.id}>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              'relative size-10 min-w-10 overflow-hidden rounded-full hover:z-10',
                              { '-mr-3': index < result.members.length - 1 },
                            )}
                          >
                            <ImageWithFallback
                              src={getUserProfilePictureUrl(user, {
                                profile: 'driver',
                              })}
                              fill
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {getDriverDisplayIdentifierDisplay(
                            user,
                            driverDisplayIdentifier,
                          )}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </TooltipProvider>

              {showSplits && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Tier / Split</span>

                  <div
                    className="rounded-full px-4 py-2 text-p-1 font-semibold"
                    style={{
                      backgroundColor: result.splits?.[0]?.color ?? '#ffffff',
                    }}
                  >
                    {result.splits?.[0]?.name ?? '-'}
                  </div>
                </div>
              )}

              {showCarClasses && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Car Classes</span>

                  <div className="flex items-start">
                    {result.carClasses.length > 0
                      ? result.carClasses.map((cc) => cc.name).join(', ')
                      : '-'}
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </TableCell>

      <TableCell className="text-end">
        <StandingsDriverPointsCell
          position={rowIndex + 1}
          points={result.total}
        />
      </TableCell>
    </TableRow>
  )
}
